import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import ApiCalls from '../../../common/ApiCalls';
import { HMapPanels } from '../const/HMapPanels';
import { useHMapContext } from '../hooks/useHMapContext';
import { ReactComponent as EditIcon } from '../../../components/1elements/Icons/svg/heatMap/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../components/1elements/Icons/svg/heatMap/delete.svg';
// import { ReactComponent as CopyIcon } from '../../../components/1elements/Icons/svg/heatMap/copy.svg'

const HMapThreeDots = ({
  id,
  name,
  type,
  allHeatMaps,
  setAllHeatMaps,
  setCurrentPanel
}) => {
  const optionsRef = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const classes = useStyles({ showOptions });
  const { mapForEditRef, currentHeatmapType, currentMapInfo, snackbar, modal } =
    useHMapContext();

  const [modalPropsRef, setModalOpen] = modal;
  const [isSnackbarOpen, setIsSnackbarOpen] = snackbar;
  const [, setHeatmapType] = currentHeatmapType;
  const [currentMap, setCurrentMap] = currentMapInfo;
  const isDefaultHeatmap = useMemo(
    () => name && name.toLowerCase() === 'default heatmap',
    [name]
  );

  const removeHeatmapFromTheList = () => {
    setAllHeatMaps(allHeatMaps.filter((item) => item.id !== id));
    currentMap === id && setCurrentMap('');
  };

  const deleteHeatmapDB = (id) => {
    // Delete
    ApiCalls()
      .deleteAHeatmap(id)
      .then((res) => {
        // console.log(res)
        setIsSnackbarOpen({
          ...isSnackbarOpen,
          message: 'You removed the heatmap successfully',
          severity: 'success',
          isOpen: true
        });
        //remove from the list
        removeHeatmapFromTheList();
        setModalOpen(false);
      })
      .catch((err) => {
        // console.log(err)
        // Snackbar
        setIsSnackbarOpen({
          ...isSnackbarOpen,
          message: err.message,
          severity: 'fail',
          isOpen: true
        });

        setModalOpen(false);
      });
  };

  const handleDeleteHeatmap = () => {
    // Prompt
    modalPropsRef.current = {
      ...modalPropsRef.current,
      type: 'deletion',
      messageText: `Are you sure about deleting the heatmap?`,
      confirmFunction: () => deleteHeatmapDB(id)
    };
    setModalOpen(true);
  };

  const handleEditHeatmap = () => {
    mapForEditRef.current = id;
    setCurrentPanel(HMapPanels.HMapDetailsEdit);

    // set heatmaptype
    setHeatmapType(type);
  };
  
  const handleClickOutside = useCallback((event) => {
    if (optionsRef.current && !optionsRef.current.contains(event.target)) {
      setShowOptions(false);
    }
  }, []);

  useEffect(() => {
    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, showOptions]);

  if (isDefaultHeatmap) {
    return <></>;
  }

  return (
    <div className={classes.root} ref={optionsRef}>
      <div
        className={classes.options}
        onClick={() => !isDefaultHeatmap && setShowOptions((prev) => !prev)}
      >
        <div></div>
        <div></div>
        <div></div>
      </div>
      {showOptions && !isDefaultHeatmap && (
        <div
          className={classes.optionsMenu}
          onMouseOver={() => setShowOptions(true)}
        >
          <div className={classes.optionsMenuWrapper}>
            <div
              className={classes.optionsMenuItem}
              onClick={handleEditHeatmap}
            >
              <EditIcon className={classes.optionsMenuItemIcon} />
              Edit
            </div>
            {/* <div className={classes.optionsMenuItem}>
              <CopyIcon className={classes.optionsMenuItemIcon} />
              Copy
            </div> */}
            <div
              className={classes.optionsMenuItem}
              onClick={handleDeleteHeatmap}
            >
              <DeleteIcon className={classes.optionsMenuItemIcon} />
              Delete
            </div>
            <div className={classes.optionMenuTriangle}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HMapThreeDots;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '25px',
    height: '25px',
    position: 'relative'
  },
  options: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: (props) => (props.minimizeMenu ? '0 6px' : '0 10px'),
    '&>div': {
      background: (props) =>
        `none repeat scroll 0 0 ${
          props.showOptions
            ? theme.palette.primary.accent
            : theme.palette.primary.main
        }`,
      height: '3px',
      width: '3px',
      lineHeight: 0,
      listStyle: 'none outside none',
      marginTop: '3px',
      verticalAlign: 'top',
      borderRadius: '50%',
      pointerEvents: 'none'
    },
    '&:hover': {
      cursor: 'pointer',
      '&>div': {
        backgroundColor: `${theme.palette.primary.accent}`
      }
    }
  },
  optionsMenu: {
    position: 'absolute',
    width: '80px',
    // height: '90px',
    top: 0,
    right: 25,
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    zIndex: 40
  },
  optionsMenuWrapper: {
    width: '100%',
    // height: '90%',
    backgroundColor: 'white',
    borderRadius: '5px',
    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.25)',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  optionsMenuItem: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '5px',
    ...theme.typography.h13,
    color: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: '#f9f9f9'
    }
  },
  optionsMenuItemIcon: {
    width: '15px',
    height: '15px',
    margin: '0 5px 0 2px'
  },
  optionMenuTriangle: {
    width: 0,
    height: 0,
    borderLeft: '10px solid white',
    borderBottom: '10px solid transparent',
    borderTop: '10px solid transparent',
    position: 'absolute',
    top: 4,
    right: -7,
    zIndex: '1'
  }
}));
